import React, { useRef, useState, useEffect } from 'react';
import { SmilePlus } from 'lucide-react';
import { useT } from '../../contexts/LocaleProvider';

const QUICK = ['👍', '❤️', '🎉', '🚀', '👀', '🙌', '😂', '🤝'];

/**
 * Inline reaction strip: existing reactions as pills + a "+ react" button
 * that opens a small picker. Optimistic · the parent's onToggle returns the
 * fresh aggregated reactions array, which we render on the next paint.
 */
export default function ReactionStrip({ reactions = [], onToggle }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const [pending, setPending] = useState(null); // emoji currently in-flight
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!picking) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setPicking(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [picking]);

  const click = async (emoji) => {
    if (pending) return;
    setPending(emoji);
    try {
      await onToggle(emoji);
    } finally {
      setPending(null);
      setPicking(false);
    }
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5 mt-3">
      {reactions.map(r => (
        <div key={r.emoji} className="relative group">
          <button
            type="button"
            onClick={() => click(r.emoji)}
            className={[
              'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[13px] border transition',
              r.mine
                ? 'bg-[hsl(var(--accent-soft))] border-[hsl(var(--accent)/.4)] text-[hsl(var(--text))]'
                : 'bg-[hsl(var(--bg-subtle))] border-border text-[hsl(var(--text-soft))] hover:border-[hsl(var(--rule-strong))] hover:text-[hsl(var(--text))]',
              pending === r.emoji ? 'opacity-50' : '',
            ].join(' ')}
          >
            <span className="text-[15px] leading-none">{r.emoji}</span>
            <span className="font-num">{r.count}</span>
          </button>
          {/* Who-reacted tooltip · below the pill (the convo modal scrolls,
              so anything above the strip would clip at the dialog edge). */}
          {(r.users?.length || 0) > 0 && (
            <div className="pointer-events-none absolute top-full left-0 mt-1.5 z-20 hidden group-hover:block group-focus-within:block">
              <div className="rounded-md bg-[hsl(var(--text))] text-[hsl(var(--bg))] text-[11px] leading-snug px-2.5 py-1.5 shadow-md w-max max-w-[240px]">
                {r.users.join(', ')}
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setPicking(o => !o)}
          aria-label={t('react.tooltip')}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[13px] border border-dashed border-border text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:border-[hsl(var(--rule-strong))]"
        >
          <SmilePlus className="h-3.5 w-3.5" />
        </button>
        {picking && (
          <div className="absolute z-10 mt-1 left-0 bg-[hsl(var(--surface))] border border-border rounded-md shadow-md p-1.5 flex gap-0.5">
            {QUICK.map(e => (
              <button
                key={e}
                type="button"
                onClick={() => click(e)}
                disabled={pending === e}
                className="text-[18px] leading-none w-8 h-8 rounded hover:bg-[hsl(var(--surface-2))] disabled:opacity-50"
                title={`${t('react.tooltip')} ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
